import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getAllPayouts, updatePayoutStatus } from '../api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, CreditCard } from 'lucide-react';

export const AdminPayoutsPage = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const res = await getAllPayouts(statusFilter || undefined);
      setPayouts(res.data.payouts || []);
    } catch {
      toast.error('Erreur chargement paiements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayouts(); }, [statusFilter]);

  const handleUpdate = async (payoutId, newStatus) => {
    if (!window.confirm(`${newStatus === 'paid' ? 'Marquer comme payé' : 'Rejeter'} ce paiement ?`)) return;
    setProcessing(true);
    try {
      await updatePayoutStatus(payoutId, newStatus, notes);
      toast.success('Paiement mis à jour');
      setSelectedPayout(null);
      setNotes('');
      fetchPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'paid') return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1 w-fit"><CheckCircle className="w-3 h-3" />Payé</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1 w-fit"><XCircle className="w-3 h-3" />Rejeté</span>;
    return <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1 w-fit"><Clock className="w-3 h-3" />En attente</span>;
  };

  const FILTERS = [
    { value: 'pending', label: 'En attente' },
    { value: 'paid', label: 'Payés' },
    { value: 'rejected', label: 'Rejetés' },
    { value: '', label: 'Tous' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Paiements créateurs</h1>

        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-foreground hover:bg-accent'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Chargement...</div>
        ) : payouts.length === 0 ? (
          <div className="py-20 text-center bg-card border border-border rounded-xl">
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-40" />
            <p className="text-muted-foreground">Aucun paiement trouvé</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Créateur</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Montant</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Méthode</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Statut</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Date</th>
                    <th className="px-4 py-3 text-right text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {payouts.map(p => (
                    <React.Fragment key={p.id}>
                      <tr className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{p.creator_username || `#${p.creator_id}`}</div>
                          <div className="text-xs text-muted-foreground">{p.payment_details}</div>
                        </td>
                        <td className="px-4 py-3 font-bold text-foreground">${p.amount}</td>
                        <td className="px-4 py-3 text-muted-foreground">{p.payment_method || '-'}</td>
                        <td className="px-4 py-3">{statusBadge(p.status)}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {p.status === 'pending' && (
                            <button
                              onClick={() => setSelectedPayout(selectedPayout?.id === p.id ? null : p)}
                              className="px-3 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                            >
                              {selectedPayout?.id === p.id ? 'Fermer' : 'Traiter'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {selectedPayout?.id === p.id && (
                        <tr>
                          <td colSpan={6} className="px-4 pb-4">
                            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                              <textarea
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                rows={2}
                                placeholder="Notes (numéro de transaction, motif de rejet...)"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                              />
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleUpdate(p.id, 'rejected')}
                                  disabled={processing}
                                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors disabled:opacity-50"
                                >
                                  <XCircle className="w-4 h-4" /> Rejeter
                                </button>
                                <button
                                  onClick={() => handleUpdate(p.id, 'paid')}
                                  disabled={processing}
                                  className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm hover:bg-green-500/20 transition-colors disabled:opacity-50"
                                >
                                  <CheckCircle className="w-4 h-4" /> Marquer payé
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
